import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { LogoUpim } from '../../components/ui-upim';
import api from '../../services/api';
import { Download, Share2, Loader2 } from 'lucide-react';

const CredencialPage = () => {
  const [credential, setCredential] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCredential = async () => {
      try {
        const response = await api.get('/dashboard/credential');
        setCredential(response.data);
      } catch (error) {
        console.error('Error fetching credential:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCredential();
  }, []);

  const handleDownload = () => {
    if (!credential?.qr_code) return;
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${credential.qr_code}`;
    link.download = `credencial-upim-${credential.member_id}.png`;
    link.click();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Mi Credencial Digital</h1>
          <p className="text-gray-600 mt-2">Presenta este código QR para identificarte como miembro UPIM</p>
        </div>

        {/* Credential Card */}
        <Card className="overflow-hidden">
          <div className="bg-gradient-to-br from-gray-800 via-gray-900 to-black p-8 text-white">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <LogoUpim className="h-12" claseTexto="text-xl" testId="credencial-logo" />
              <Badge className="bg-white/20 text-white border-0">
                Miembro Activo
              </Badge>
            </div>

            {/* QR Code */}
            <div className="flex justify-center mb-8">
              {credential?.qr_code && (
                <div className="bg-white p-4 rounded-xl">
                  <img 
                    src={`data:image/png;base64,${credential.qr_code}`}
                    alt="QR Credencial"
                    className="w-48 h-48"
                  />
                </div>
              )}
            </div>

            {/* Member Info */}
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold">{credential?.name}</h2>
              <p className="text-gray-300">{credential?.company || 'Asesor Inmobiliario'}</p>
              <p className="text-sm text-gray-400">{credential?.email}</p>
            </div>

            {/* Member ID */}
            <div className="mt-6 pt-6 border-t border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400 uppercase">ID Miembro</p>
                  <p className="font-mono font-bold">{credential?.member_id}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400 uppercase">Membresía</p>
                  <p className="font-semibold">{credential?.membership?.plan || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Validity */}
            {credential?.membership?.valid_until && (
              <div className="mt-4 p-3 bg-white/10 rounded-lg">
                <p className="text-xs text-gray-400 uppercase text-center">Válida hasta</p>
                <p className="font-semibold text-center">
                  {new Date(credential.membership.valid_until).toLocaleDateString('es-MX', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
            )}
          </div>

          <CardContent className="p-6 bg-gray-50">
            <div className="flex space-x-3">
              <Button 
                className="flex-1 bg-gray-900 hover:bg-gray-800"
                onClick={handleDownload}
              >
                <Download className="mr-2 h-4 w-4" />
                Descargar QR
              </Button>
              <Button variant="outline" className="flex-1">
                <Share2 className="mr-2 h-4 w-4" />
                Compartir
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4">¿Cómo usar tu credencial?</h3>
            <ul className="space-y-3 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold mr-3">1</span>
                Presenta el código QR en eventos y capacitaciones UPIM para tu registro
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold mr-3">2</span>
                Muestra tu credencial a proveedores certificados para acceder a descuentos
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold mr-3">3</span>
                Descarga tu QR para tenerlo disponible sin conexión a internet
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default CredencialPage;
